import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCurrentEpisode,
  getEpisodeLupa,
  listEpisodesForPatient,
  type EpisodeLupaPeriodResponse,
} from '../services/api';
import type {
  DeletedTaskResponse,
  EpisodeSummaryResponse,
  TaskListResponse,
} from '../services/api/types.gen';
import {
  currentEpisodeToAddTaskTarget,
  type AddTaskTargetEpisode,
} from '../components/patient/patientDetailTaskConstants';

/**
 * Episode loading and selection for the patient chart.
 *
 * Split in two because the page's two data hooks depend on each other: the task
 * hook needs the selected episode to scope its fetches, and the pieces below
 * that build the Add Task target need the tasks (a task row is where the
 * episode's clinic and physician come from). Everything that does NOT need the
 * tasks lives in `usePatientDetailEpisodes`, which the page calls first;
 * `useAddTaskTargetEpisode` is called after the task hook with both halves in
 * hand. Effect order inside each hook matches the order the page declared them
 * in before the split.
 */
export interface UsePatientDetailEpisodesParams {
  patientId: string | undefined;
  episodeIdFromUrl: string | null;
  /** Tracks the patient the page is currently on, for late-resolving fetches. */
  currentPatientIdRef: React.MutableRefObject<string | undefined>;
}

export interface PatientDetailEpisodesApi {
  episodes: EpisodeSummaryResponse[];
  setEpisodes: React.Dispatch<React.SetStateAction<EpisodeSummaryResponse[]>>;
  canAddEpisode: boolean;
  /**
   * Which patient's episode fetch has finished, successfully or not. Anything
   * that decides what to *show* from the selected episode needs this: without
   * it, "no episode" and "not loaded yet" are the same value, and on the first
   * render after the patient changes `episodes` still holds the previous
   * patient's rows because the clearing effect below has not run yet.
   */
  episodesSettledPatientId: string | null;
  selectedEpisodeId: string | null;
  setSelectedEpisodeId: React.Dispatch<React.SetStateAction<string | null>>;
  effectiveSelectedEpisodeId: string | null;
  selectedEpisode: EpisodeSummaryResponse | null;
  previousEpisode: EpisodeSummaryResponse | null;
  otherEpisodes: EpisodeSummaryResponse[];
  lupaPeriods: EpisodeLupaPeriodResponse[];
  lupaLoading: boolean;
  loadEpisodes: () => Promise<void>;
}

export function usePatientDetailEpisodes({
  patientId,
  episodeIdFromUrl,
  currentPatientIdRef,
}: UsePatientDetailEpisodesParams): PatientDetailEpisodesApi {
  const [episodes, setEpisodes] = useState<EpisodeSummaryResponse[]>([]);
  const [canAddEpisode, setCanAddEpisode] = useState(false);
  const [episodesSettledPatientId, setEpisodesSettledPatientId] = useState<
    string | null
  >(null);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(
    null
  );
  // Kept apart from `lupaPeriods.length`: an episode with no determinable
  // thresholds and an episode still fetching them both read as empty, and only
  // one of them should hold the strip's space open (EMR-1161).
  const [lupaLoading, setLupaLoading] = useState(false);
  const [lupaPeriods, setLupaPeriods] = useState<EpisodeLupaPeriodResponse[]>(
    []
  );
  const previousPatientIdRef = useRef(patientId);

  // Clear patient-scoped state before fetching so stale episodes from the
  // previous patient cannot bleed into PatientHeader. Declared before the fetch
  // below, matching the order the page ran these in.
  useEffect(() => {
    if (!patientId) return;
    setEpisodes([]);
    setCanAddEpisode(false);
  }, [patientId]);

  const loadEpisodes = useCallback(async () => {
    if (!patientId) return;
    const requestedPatientId = patientId;

    try {
      const response = await listEpisodesForPatient({
        path: { patient_id: requestedPatientId },
      });
      if (currentPatientIdRef.current !== requestedPatientId) {
        return;
      }
      setEpisodes(response.data?.episodes ?? []);
      setCanAddEpisode(response.data?.can_add_episode ?? false);
    } catch (err) {
      if (currentPatientIdRef.current !== requestedPatientId) {
        return;
      }
      console.error('Error fetching episodes:', err);
      setEpisodes([]);
      setCanAddEpisode(false);
    } finally {
      if (currentPatientIdRef.current === requestedPatientId) {
        setEpisodesSettledPatientId(requestedPatientId);
      }
    }
  }, [currentPatientIdRef, patientId]);

  useEffect(() => {
    void loadEpisodes();
  }, [loadEpisodes]);

  useEffect(() => {
    if (!episodeIdFromUrl || episodes.length === 0) {
      return;
    }
    if (episodes.some(episode => episode.id === episodeIdFromUrl)) {
      setSelectedEpisodeId(episodeIdFromUrl);
    }
  }, [episodeIdFromUrl, episodes]);

  useEffect(() => {
    setEpisodesSettledPatientId(null);
  }, [patientId]);

  // Declared after the URL sync above so a patient switch always lands on the
  // newest episode rather than one the previous URL named.
  useEffect(() => {
    if (previousPatientIdRef.current === patientId) {
      return;
    }
    previousPatientIdRef.current = patientId;
    setSelectedEpisodeId(null);
  }, [patientId]);

  const effectiveSelectedEpisodeId = useMemo(() => {
    if (!episodes.length) return null;
    if (selectedEpisodeId && episodes.find(e => e.id === selectedEpisodeId))
      return selectedEpisodeId;
    return episodes[0].id;
  }, [episodes, selectedEpisodeId]);

  const selectedEpisode = useMemo(
    () => episodes.find(e => e.id === effectiveSelectedEpisodeId) ?? null,
    [episodes, effectiveSelectedEpisodeId]
  );

  // ROG-2493: per-30-day-period LUPA thresholds for the selected episode.
  useEffect(() => {
    setLupaPeriods([]);
    if (!effectiveSelectedEpisodeId) {
      setLupaLoading(false);
      return;
    }
    let cancelled = false;
    setLupaLoading(true);
    getEpisodeLupa({
      path: { episode_id: effectiveSelectedEpisodeId },
    })
      .then(response => {
        if (!cancelled) setLupaPeriods(response.data?.periods ?? []);
      })
      .catch(error => {
        // Non-blocking: the supplementary LUPA strip just stays hidden.
        console.error('Failed to load LUPA thresholds', error);
        if (!cancelled) setLupaPeriods([]);
      })
      .finally(() => {
        if (!cancelled) setLupaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedEpisodeId]);

  const previousEpisode = useMemo(() => {
    if (episodes.length === 0) return null;
    return episodes.reduce((latest, episode) =>
      latest.end_date > episode.end_date ? latest : episode
    );
  }, [episodes]);

  // All episodes other than the currently selected one, in the same
  // newest-first order as `episodes`. Surfaced behind a subtle "View other
  // episodes" dropdown since switching episodes is rare.
  const otherEpisodes = useMemo(
    () => episodes.filter(e => e.id !== effectiveSelectedEpisodeId),
    [episodes, effectiveSelectedEpisodeId]
  );

  return {
    episodes,
    setEpisodes,
    canAddEpisode,
    episodesSettledPatientId,
    selectedEpisodeId,
    setSelectedEpisodeId,
    effectiveSelectedEpisodeId,
    selectedEpisode,
    previousEpisode,
    otherEpisodes,
    lupaPeriods,
    lupaLoading,
    loadEpisodes,
  };
}

export interface UseAddTaskTargetEpisodeParams {
  patientId: string | undefined;
  episodeIdFromUrl: string | null;
  selectedVisitId: string | null;
  episodes: EpisodeSummaryResponse[];
  selectedEpisode: EpisodeSummaryResponse | null;
  effectiveSelectedEpisodeId: string | null;
  setSelectedEpisodeId: React.Dispatch<React.SetStateAction<string | null>>;
  tasks: TaskListResponse[];
  linkedDeletedTask: DeletedTaskResponse | null;
  visitsLoading: boolean;
  tasksPatientId: string | null;
  currentPatientIdRef: React.MutableRefObject<string | undefined>;
}

export interface AddTaskTargetEpisodeApi {
  addTaskTargetEpisode: AddTaskTargetEpisode | null;
  addTaskTargetClinicId: string | undefined;
}

export function useAddTaskTargetEpisode({
  patientId,
  episodeIdFromUrl,
  selectedVisitId,
  episodes,
  selectedEpisode,
  effectiveSelectedEpisodeId,
  setSelectedEpisodeId,
  tasks,
  linkedDeletedTask,
  visitsLoading,
  tasksPatientId,
  currentPatientIdRef,
}: UseAddTaskTargetEpisodeParams): AddTaskTargetEpisodeApi {
  const [currentEpisodeFallbackState, setCurrentEpisodeFallbackState] =
    useState<{
      patientId: string;
      episode: AddTaskTargetEpisode | null;
    } | null>(null);
  // The last visit id the episode header was moved onto. Kept so the move
  // happens once per visit: without it the effect could not tell its own
  // pinned episode from one the user picked, and would either fight the
  // header dropdown or refuse to follow a second visit in the same mount.
  const followedVisitEpisodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!patientId) return;
    setCurrentEpisodeFallbackState(null);
  }, [patientId]);

  // A visit deep link carries a task id and no episode, so the page would sit
  // on the newest episode while the URL names a visit in an older one: closing
  // the visit drops the user into an episode they never asked for, and the
  // header's insurance and authorization context describes the wrong one.
  // Move the header onto the visit's episode instead. Once per visit, so that
  // switching the header afterwards is not snapped back, and never over an
  // episode the URL named itself.
  useEffect(() => {
    if (episodeIdFromUrl || !selectedVisitId) return;
    if (followedVisitEpisodeIdRef.current === selectedVisitId) return;
    const linkedTask =
      tasks.find(task => task.id === selectedVisitId) ??
      (linkedDeletedTask?.id === selectedVisitId ? linkedDeletedTask : null);
    // Not yet resolvable: the tasks or the episodes are still loading, so leave
    // the ref alone and follow the visit once they arrive.
    if (!linkedTask) return;
    if (!episodes.some(episode => episode.id === linkedTask.episode_id)) return;
    followedVisitEpisodeIdRef.current = selectedVisitId;
    // Already the episode on screen. Pinning it would only stop the header
    // following the episode list, which matters when one is added.
    if (linkedTask.episode_id === effectiveSelectedEpisodeId) return;
    setSelectedEpisodeId(linkedTask.episode_id);
  }, [
    episodeIdFromUrl,
    effectiveSelectedEpisodeId,
    selectedVisitId,
    tasks,
    linkedDeletedTask,
    episodes,
    setSelectedEpisodeId,
  ]);

  const currentEpisodeFallback =
    currentEpisodeFallbackState &&
    currentEpisodeFallbackState.patientId === patientId
      ? currentEpisodeFallbackState.episode
      : null;

  useEffect(() => {
    let cancelled = false;
    const requestedPatientId = patientId;

    const loadCurrentEpisodeFallback = async () => {
      if (
        !requestedPatientId ||
        visitsLoading ||
        tasksPatientId !== requestedPatientId
      ) {
        return;
      }
      if (episodes.length > 0) {
        setCurrentEpisodeFallbackState(null);
        return;
      }

      try {
        const response = await getCurrentEpisode({
          path: { patient_id: requestedPatientId },
        });
        if (!cancelled && currentPatientIdRef.current === requestedPatientId) {
          setCurrentEpisodeFallbackState({
            patientId: requestedPatientId,
            episode: currentEpisodeToAddTaskTarget(response.data ?? null),
          });
        }
      } catch (error) {
        console.error(
          'Failed to load current episode for Add Task bounds:',
          error
        );
        if (!cancelled && currentPatientIdRef.current === requestedPatientId) {
          setCurrentEpisodeFallbackState({
            patientId: requestedPatientId,
            episode: null,
          });
        }
      }
    };

    void loadCurrentEpisodeFallback();

    return () => {
      cancelled = true;
    };
  }, [
    currentPatientIdRef,
    episodes.length,
    patientId,
    tasksPatientId,
    visitsLoading,
  ]);

  const taskDerivedLatestEpisode = episodes[0] ?? null;

  const addTaskTargetEpisode = useMemo<AddTaskTargetEpisode | null>(() => {
    if (selectedEpisode) {
      return {
        id: selectedEpisode.id,
        start_date: selectedEpisode.start_date,
        end_date: selectedEpisode.end_date,
        status: selectedEpisode.status,
        agency_clinic_id:
          tasks.find(t => t.episode_id === selectedEpisode.id)
            ?.agency_clinic_id ?? undefined,
        episode_physician_id:
          tasks.find(t => t.episode_id === selectedEpisode.id)
            ?.episode_physician_id ?? undefined,
      };
    }
    if (taskDerivedLatestEpisode) {
      return {
        id: taskDerivedLatestEpisode.id,
        start_date: taskDerivedLatestEpisode.start_date,
        end_date: taskDerivedLatestEpisode.end_date,
        status: taskDerivedLatestEpisode.status,
        agency_clinic_id:
          tasks.find(t => t.episode_id === taskDerivedLatestEpisode.id)
            ?.agency_clinic_id ?? undefined,
        episode_physician_id:
          tasks.find(t => t.episode_id === taskDerivedLatestEpisode.id)
            ?.episode_physician_id ?? undefined,
      };
    }
    return currentEpisodeFallback;
  }, [
    currentEpisodeFallback,
    selectedEpisode,
    taskDerivedLatestEpisode,
    tasks,
  ]);

  return {
    addTaskTargetEpisode,
    addTaskTargetClinicId: addTaskTargetEpisode?.agency_clinic_id ?? undefined,
  };
}
